import { useDispatch, useSelector } from "react-redux";
import { useRestaurantDetails } from "../providers/RestaurantDetailsContext";
import { useTranslation } from 'react-i18next';
import { changeMobileDrawerMenuShowState, setShowSendMenuLinkModal, toggleSelectOptionsModal, toggleShowSettingsModal } from '../../store/slices/appGeneralSlice';
import { toggleOpenPaymentsModal } from '../../store/slices/paymentManagementSlice';
import { useEffect, useState } from 'react';
import { LANGUAGES } from '../../constants';
import SelectOptionsModal from '../../components/Layouts/SelectOptionsModal';
import logoIcon from './../../components/images/app-icon-circle.png';
import { useAuth } from '../providers/AuthContext';

import './MobileDrawerMenu.scss';


const MobileDrawerMenu = () => {
    const { i18n, t } = useTranslation();

    const initialLanguageCode = localStorage.getItem("langCode") || 'en';
    const selectedLanguageOption = LANGUAGES.find(lang => lang.value === initialLanguageCode);

    const { restaurantDetails } = useRestaurantDetails();

    const {
        restaurant_logo,
        waiter_name,
        waiter_email,
        waiter_permissions,
    } = restaurantDetails || {};

    const canViewOrdersReport = waiter_permissions?.orders_report !== false;

    const dispatch = useDispatch();

    const {
        logout
    } = useAuth();

    const showMenu = useSelector((state: RootState) => state.appGeneral.showMobileDrawerMenu);

    const [drawerState, setDrawerState] = useState("closed");


    useEffect(() => {
        return () => {
            dispatch(changeMobileDrawerMenuShowState({
                show: undefined
            }));
        }
    }, []);

    useEffect(() => {
        if (showMenu !== undefined && showMenu !== null) {
            if (showMenu && (drawerState === "closed" || drawerState === "closing")) {
                setDrawerState("opening");
            }
            else if (drawerState === "opened" || drawerState === "opening") {
                setDrawerState("closing")
            }
        }
    }, [showMenu]);


    const handleCloseMobileDrawerMenu = () => {
        dispatch(changeMobileDrawerMenuShowState({
            show: false
        }));
    };

    const handleShowOpenPeysModal = () => {
        handleCloseMobileDrawerMenu();

        dispatch(toggleOpenPaymentsModal({
            showModal: true,
        }));
    };

    const handleShowLanguageSelectOptions = () => {
        dispatch(toggleSelectOptionsModal({
            showModal: true,
            selectTitle: String(t("chooseLanguage")),
            selectOptions: LANGUAGES
        }));
    };
    
    const handleSelectLanguageOption = (lang_code: string) => {
        localStorage.setItem('langCode', lang_code);
        i18n.changeLanguage(lang_code);
        // window.location.reload(false);
        window.location.reload();
    };

    const handleDrawerTransitionEnd = (evt) => {
        if (drawerState === "opening") {
            setDrawerState("opened");
        }
        else if (drawerState === "closing") {
            setDrawerState("closed");
        }
    };

    const handleUserLogout = (e) => {
        e.preventDefault();
        logout();
    };

    const handleOpenSettingsModal = () => {
        dispatch(toggleShowSettingsModal({
            showModal: true
        }));
    };

    const handleOpenSendMenuLinkModal = () => {
        dispatch(setShowSendMenuLinkModal(true))
    };


    return (
        <>
            <div
                className={`mobile-drawer-menu-overlay is-${drawerState}`}
                style={{
                    display: `${(drawerState === 'closed' && !showMenu) ? 'none' : ''}`
                }}
                onClick={handleCloseMobileDrawerMenu}
            ></div>
            <div
                className={`mobile-drawer-menu-wrapper is-${drawerState}`}
                onTransitionEnd={handleDrawerTransitionEnd}
                style={{
                    display: `${(drawerState === 'closed' && !showMenu) ? 'none' : ''}`
                }}
            >
                <div className="mobile-drawer-menu d-flex flex-column">
                    <div className="menu-header d-flex align-items-center">
                        <span className="user-avatar me-3">
                            <img
                                src={restaurant_logo || logoIcon}
                                width="40"
                                height="40"
                                alt={String(t("appLogoAlt"))}
                            />
                        </span>
                        <div className="user-general-info">
                            <div className="fs-5 fw-bold">
                                {waiter_name}
                            </div>
                            <div>
                                {waiter_email}
                            </div>
                        </div>
                    </div>
                    <div className="menu-body d-flex flex-column flex-grow-1">
                        <ul className="menu-items-list flex-grow-1 mb-0">
                            {canViewOrdersReport && (
                                <li>
                                    <div
                                        className="menu-item-link"
                                        onClick={handleShowOpenPeysModal}
                                    >
                                        {/* {t("openPaymentsReport")} */}
                                        {/* {t("report")} */}
                                        {t("openPayments")}
                                    </div>
                                </li>
                            )}
                            <li>
                                <div
                                    className="menu-item-link d-flex justify-content-between align-items-center"
                                    onClick={handleShowLanguageSelectOptions}
                                >
                                    <span>
                                        {t("language")}
                                    </span>
                                    <span className="text-uppercase ms-3">
                                        {selectedLanguageOption?.code || ''}
                                    </span>
                                </div>
                            </li>
                            <li>
                                <div
                                    className="menu-item-link"
                                    onClick={handleOpenSettingsModal}
                                >
                                    <span>
                                        {t("settings")}
                                    </span>
                                </div>
                            </li>
                            <li>
                                <div
                                    className="menu-item-link"
                                    onClick={handleOpenSendMenuLinkModal}
                                >
                                    <span>
                                        {t("sendMenuSms")}
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div className="menu-footer mb-1">
                        <button
                            className="btn log-out-btn"
                            onClick={handleUserLogout}
                        >
                            <span className="icon me-2">
                                <svg width="22" height="24" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" fill="currentColor"><path d="M228.66 164.081c19.426-14.6 47.01-10.688 61.61 8.737 14.6 19.426 10.688 47.009-8.737 61.61C185.55 306.567 128 419.264 128 541.607c0 212.077 171.923 384 384 384s384-171.923 384-384c0-122.476-57.676-235.281-153.834-307.407-19.44-14.58-23.378-42.16-8.797-61.6 14.581-19.44 42.16-23.378 61.6-8.797C913.054 252.375 984 391.136 984 541.608c0 260.678-211.322 472-472 472s-472-211.322-472-472c0-150.31 70.792-288.937 188.66-377.527zM556 384c0 24.3-19.7 44-44 44s-44-19.7-44-44V75c0-24.3 19.7-44 44-44s44 19.7 44 44v309z"/></svg>
                            </span>
                            {t("logout")}
                        </button>
                    </div>
                </div>
            </div>

            <SelectOptionsModal
                handleSelectOption={handleSelectLanguageOption}
                selectedValue={selectedLanguageOption?.value}
            />
        </>
    )
}

export default MobileDrawerMenu;
